import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BlockNoteEditor } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { Box, Button, TextField, Stack, Paper, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useAccessToken } from '@features/user/store/hooks';
import { notesApi } from '@features/notes/store/api';
import { usePromptExplanationStream } from '@features/notes/hooks/usePromptExplanationStream';
import ThinkingAnimation from '@components/Common/ThinkingAnimation';

import '@blocknote/mantine/style.css';
import '@blocknote/core/fonts/inter.css';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const STORAGE_KEYS = {
  MODULE_ID: 'sandbox_module_id',
  NOTE_ID: 'sandbox_note_id',
  FEEDBACK: 'sandbox_feedback',
  THREAD_ID: 'sandbox_thread_id',
  CUSTOMIZE_THREAD_ID: 'sandbox_customize_thread_id',
} as const;

interface SSEEvent {
  type: string;
  content?: string;
  delta?: string;
  error?: string;
  namespace?: 'document' | 'prompt' | 'prompt_explanation' | 'assistant';
}

export default function ModuleGenerationSandbox() {
  // Get module_id from URL params
  const { moduleId: urlModuleId } = useParams<{ moduleId?: string }>();
  
  // Load from URL params first, then sessionStorage as fallback
  const [moduleId, setModuleId] = useState(() => {
    if (urlModuleId) {
      return urlModuleId;
    }
    try {
      return sessionStorage.getItem(STORAGE_KEYS.MODULE_ID) || '';
    } catch {
      return '';
    }
  });

  // Update moduleId when URL param changes
  useEffect(() => {
    if (urlModuleId) {
      setModuleId(urlModuleId);
    }
  }, [urlModuleId]);

  const [noteId, setNoteId] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEYS.NOTE_ID) || '';
    } catch {
      return '';
    }
  });
  const [feedback, setFeedback] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEYS.FEEDBACK) || '';
    } catch {
      return '';
    }
  });
  const [threadId, setThreadId] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEYS.THREAD_ID) || '';
    } catch {
      return '';
    }
  });
  const [customizeThreadId, setCustomizeThreadId] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEYS.CUSTOMIZE_THREAD_ID) || '';
    } catch {
      return '';
    }
  });
  const [content, setContent] = useState(''); // Document namespace content
  const [promptContent, setPromptContent] = useState(''); // Prompt namespace content
  const [promptExplanationContent, setPromptExplanationContent] = useState(''); // Prompt explanation namespace content
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rightPanelWidth, setRightPanelWidth] = useState(400);
  const [leftPanelWidth, setLeftPanelWidth] = useState(300);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [loadedVersionNumber, setLoadedVersionNumber] = useState<number | null>(null);
  
  const {
    thinkingVerbs,
    explanationText,
    isStreaming: isExplanationStreaming,
    reset: resetExplanationStream,
    processChunk: processExplanationChunk,
  } = usePromptExplanationStream();
  const editorRef = useRef<BlockNoteEditor | null>(null); // Main document editor
  const promptEditorRef = useRef<BlockNoteEditor | null>(null); // Prompt editor
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingUpdateTimer = useRef<NodeJS.Timeout | null>(null);
  const promptStreamingUpdateTimer = useRef<NodeJS.Timeout | null>(null);
  const lastContent = useRef('');
  const lastPromptContent = useRef('');
  const currentContentRef = useRef(''); // Track current content for flushing
  const currentPromptContentRef = useRef(''); // Track current prompt content for flushing
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { getAccessToken } = useAccessToken();

  // Fetch version history using customize thread_id
  const {
    data: versionHistoryData,
    refetch: refetchVersionHistory,
  } = notesApi.useGetV2ProviderNoteGenerationCustomizeHistoryByThreadIdQuery(
    { threadId: customizeThreadId || '' },
    { skip: !customizeThreadId },
  );

  const [generateModule] = notesApi.usePostV2ProviderNoteGenerationGenerateMutation();
  const [customizeModule] = notesApi.usePostV2ProviderNoteGenerationCustomizeMutation();
  const [applyCustomization] = notesApi.usePostV2ProviderNoteGenerationCustomizeApplyByThreadIdMutation();

  // Create main document editor instance
  const editor = useMemo(() => {
    const editorInstance = BlockNoteEditor.create({
      initialContent: undefined,
    });
    editorRef.current = editorInstance;
    return editorInstance;
  }, []);

  // Create prompt editor instance
  const promptEditor = useMemo(() => {
    const editorInstance = BlockNoteEditor.create({
      initialContent: undefined,
    });
    promptEditorRef.current = editorInstance;
    return editorInstance;
  }, []);

  // Function to update document editor with content
  const updateEditorContent = useCallback(async (contentToUpdate: string) => {
    if (!editorRef.current) return;

    try {
      const blocks = await editorRef.current.tryParseMarkdownToBlocks(contentToUpdate);
      editorRef.current.replaceBlocks(editorRef.current.document, blocks);
      lastContent.current = contentToUpdate;

      // Auto-scroll to bottom during streaming
      const editorElement = editorRef.current._tiptapEditor?.view?.dom;
      if (editorElement && editorElement.parentElement) {
        editorElement.parentElement.scrollTop = editorElement.parentElement.scrollHeight;
      }
    } catch (error) {
      // Fallback: create simple paragraph blocks
      try {
        const lines = contentToUpdate.split('\n');
        const fallbackBlocks = lines.map(line => ({
          type: 'paragraph' as const,
          content: line || ' ',
        }));
        editorRef.current.replaceBlocks(editorRef.current.document, fallbackBlocks as any);
        lastContent.current = contentToUpdate;
      } catch (fallbackError) {
        console.error('Failed to update editor:', fallbackError);
      }
    }
  }, []);

  // Function to update prompt editor with content
  const updatePromptEditorContent = useCallback(async (contentToUpdate: string) => {
    if (!promptEditorRef.current) return;

    try {
      const blocks = await promptEditorRef.current.tryParseMarkdownToBlocks(contentToUpdate);
      promptEditorRef.current.replaceBlocks(promptEditorRef.current.document, blocks);
      lastPromptContent.current = contentToUpdate;

      // Auto-scroll to bottom during streaming
      const editorElement = promptEditorRef.current._tiptapEditor?.view?.dom;
      if (editorElement && editorElement.parentElement) {
        editorElement.parentElement.scrollTop = editorElement.parentElement.scrollHeight;
      }
    } catch (error) {
      // Fallback: create simple paragraph blocks
      try {
        const lines = contentToUpdate.split('\n');
        const fallbackBlocks = lines.map(line => ({
          type: 'paragraph' as const,
          content: line || ' ',
        }));
        promptEditorRef.current.replaceBlocks(promptEditorRef.current.document, fallbackBlocks as any);
        lastPromptContent.current = contentToUpdate;
      } catch (fallbackError) {
        console.error('Failed to update prompt editor:', fallbackError);
      }
    }
  }, []);

  // Function to load a version into all fields
  const loadVersion = useCallback(async (version: any, versionNumber: number) => {
    try {
      // Load examples into document editor (use first example or join all)
      if (version.examples && Array.isArray(version.examples) && version.examples.length > 0) {
        const exampleContent = version.examples[0]; // Use first example
        await updateEditorContent(exampleContent);
        setContent(exampleContent);
      }

      // Load prompt into prompt editor
      if (version.prompt) {
        await updatePromptEditorContent(version.prompt);
        setPromptContent(version.prompt);
      }

      // Load explanation into prompt explanation
      if (version.explanation) {
        setPromptExplanationContent(version.explanation);
        // Reset and process explanation to extract thinking verbs and text
        resetExplanationStream();
        processExplanationChunk(version.explanation);
      }

      // Load feedback into feedback field
      if (version.feedback) {
        setFeedback(version.feedback);
      }

      // Track which version is loaded
      setLoadedVersionNumber(versionNumber);
    } catch (error) {
      console.error('Failed to load version:', error);
    }
  }, [updateEditorContent, updatePromptEditorContent, processExplanationChunk, resetExplanationStream]);

  // Function to apply/save the currently loaded version
  const handleSaveVersion = useCallback(async () => {
    if (!customizeThreadId) {
      setError('No customize thread ID available');
      return;
    }

    if (!loadedVersionNumber) {
      setError('No version loaded. Please load a version first.');
      return;
    }

    try {
      setError(null);
      const response = await applyCustomization({
        threadId: customizeThreadId,
        applyCustomizationRequest: {
          version: loadedVersionNumber,
        },
      }).unwrap();

      // Refetch version history to get updated state
      refetchVersionHistory();

      // Clear loaded version number after saving
      setLoadedVersionNumber(null);

      // Show success message or handle response
      console.log('Version applied:', response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply version');
    }
  }, [customizeThreadId, loadedVersionNumber, applyCustomization, refetchVersionHistory]);

  // Handle streaming content updates with debouncing
  useEffect(() => {
    if (isStreaming && editorRef.current && content !== lastContent.current) {
      if (streamingUpdateTimer.current) {
        clearTimeout(streamingUpdateTimer.current);
      }

      streamingUpdateTimer.current = setTimeout(() => {
        updateEditorContent(content);
      }, 50);
    }

    return () => {
      if (streamingUpdateTimer.current) {
        clearTimeout(streamingUpdateTimer.current);
      }
    };
  }, [content, isStreaming, updateEditorContent]);

  // Handle prompt streaming content updates with debouncing
  useEffect(() => {
    if (isStreaming && promptEditorRef.current && promptContent !== lastPromptContent.current) {
      if (promptStreamingUpdateTimer.current) {
        clearTimeout(promptStreamingUpdateTimer.current);
      }

      promptStreamingUpdateTimer.current = setTimeout(() => {
        updatePromptEditorContent(promptContent);
      }, 50);
    }

    return () => {
      if (promptStreamingUpdateTimer.current) {
        clearTimeout(promptStreamingUpdateTimer.current);
      }
    };
  }, [promptContent, isStreaming, updatePromptEditorContent]);

  // Flush remaining content when streaming stops
  useEffect(() => {
    if (!isStreaming && content && content !== lastContent.current && editorRef.current) {
      // Clear any pending timer and immediately update
      if (streamingUpdateTimer.current) {
        clearTimeout(streamingUpdateTimer.current);
        streamingUpdateTimer.current = null;
      }
      updateEditorContent(content);
    }
    if (!isStreaming && promptContent && promptContent !== lastPromptContent.current && promptEditorRef.current) {
      // Clear any pending timer and immediately update
      if (promptStreamingUpdateTimer.current) {
        clearTimeout(promptStreamingUpdateTimer.current);
        promptStreamingUpdateTimer.current = null;
      }
      updatePromptEditorContent(promptContent);
    }
  }, [isStreaming, content, promptContent, updateEditorContent, updatePromptEditorContent]);

  const handleStream = useCallback(
    async (streamUrl: string) => {
      const token = await getAccessToken();
      if (!token) {
        setError('Failed to get access token');
        return;
      }

      abortControllerRef.current = new AbortController();
      setIsStreaming(true);
      setContent('');
      setPromptContent('');
      setPromptExplanationContent('');
      setError(null);
      lastContent.current = '';
      lastPromptContent.current = '';
      currentContentRef.current = '';
      currentPromptContentRef.current = '';

      try {
        await fetchEventSource(`${API_BASE_URL}${streamUrl}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: abortControllerRef.current.signal,

          onopen: async response => {
            if (!response.ok || response.status !== 200) {
              throw new Error(`Failed to connect: ${response.status} ${response.statusText}`);
            }
          },

          onmessage: event => {
            try {
              const data: SSEEvent = JSON.parse(event.data);
              
              switch (data.type) {
                case 'TEXT_MESSAGE_START':
                  // Resume streaming when a new message starts (e.g., document namespace after prompt_explanation)
                  setIsStreaming(true);
                  break;
                case 'TEXT_MESSAGE_CONTENT':
                  // Ensure streaming is active when content arrives
                  setIsStreaming(true);
                  if (data.delta) {
                    const namespace = data.namespace || 'document'; // Default to document if not specified
                    if (namespace === 'prompt') {
                      setPromptContent(prev => {
                        const newContent = prev + data.delta;
                        currentPromptContentRef.current = newContent; // Keep ref in sync
                        return newContent;
                      });
                    } else if (namespace === 'prompt_explanation') {
                      setPromptExplanationContent(prev => prev + data.delta);
                      processExplanationChunk(data.delta);
                    } else {
                      // Default to document namespace
                      setContent(prev => {
                        const newContent = prev + data.delta;
                        currentContentRef.current = newContent; // Keep ref in sync
                        return newContent;
                      });
                    }
                  }
                  break;
                case 'TEXT_MESSAGE_END':
                  // Flush any pending content updates before stopping
                  // Clear any pending timers first
                  if (streamingUpdateTimer.current) {
                    clearTimeout(streamingUpdateTimer.current);
                    streamingUpdateTimer.current = null;
                  }
                  if (promptStreamingUpdateTimer.current) {
                    clearTimeout(promptStreamingUpdateTimer.current);
                    promptStreamingUpdateTimer.current = null;
                  }
                  // Immediately update with the current content from refs
                  const finalContent = currentContentRef.current;
                  if (finalContent && finalContent !== lastContent.current && editorRef.current) {
                    updateEditorContent(finalContent);
                  }
                  const finalPromptContent = currentPromptContentRef.current;
                  if (finalPromptContent && finalPromptContent !== lastPromptContent.current && promptEditorRef.current) {
                    updatePromptEditorContent(finalPromptContent);
                  }
                  // Note: Don't set isImproving to false here - wait for RUN_FINISHED
                  // Only stop streaming for this specific message
                  setIsStreaming(false);
                  break;
                case 'RUN_FINISHED':
                case 'completion':
                  // Entire run is complete, stop improving state
                  setIsStreaming(false);
                  setIsImproving(false);
                  // Refetch version history after run completes
                  if (customizeThreadId) {
                    refetchVersionHistory();
                  }
                  break;
                case 'RUN_ERROR':
                case 'error':
                  setError(data.error || 'Streaming error occurred');
                  setIsStreaming(false);
                  setIsImproving(false);
                  break;
                case 'STEP_FINISHED':
                case 'STEP_STARTED':
                  // Keep improving state active during steps
                  // These events just indicate progress, don't change loading state
                  break;
                case 'token':
                case 'content':
                  if (data.content) {
                    const namespace = data.namespace || 'document'; // Default to document if not specified
                    if (namespace === 'prompt') {
                      setPromptContent(prev => {
                        const newContent = prev + data.content;
                        currentPromptContentRef.current = newContent; // Keep ref in sync
                        return newContent;
                      });
                    } else if (namespace === 'prompt_explanation') {
                      setPromptExplanationContent(prev => prev + data.content);
                      processExplanationChunk(data.content);
                    } else {
                      setContent(prev => {
                        const newContent = prev + data.content;
                        currentContentRef.current = newContent; // Keep ref in sync
                        return newContent;
                      });
                    }
                  }
                  break;
              }
            } catch (parseError) {
              // Silently ignore parse errors
            }
          },

          onerror: error => {
            setError('Streaming connection failed');
            setIsStreaming(false);
            setIsImproving(false);
            throw error;
          },
        });
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err.message || 'Failed to connect to streaming service');
        }
        setIsStreaming(false);
        setIsImproving(false);
      }
    },
    [getAccessToken, updateEditorContent, updatePromptEditorContent, isImproving, processExplanationChunk, customizeThreadId, refetchVersionHistory],
  );

  const handleSubmit = useCallback(async () => {
    if (!moduleId.trim() || !noteId.trim()) {
      setError('Please provide both module_id and note_id');
      return;
    }

    try {
      setError(null);
      setContent('');
      setPromptContent('');
      setPromptExplanationContent('');
      setIsStreaming(false);
      setLoadedVersionNumber(null); // Clear loaded version when starting new generate

      // Get prompt content from editor if available
      let moduleParam: { generation_prompt: string } | undefined;
      if (promptEditorRef.current) {
        try {
          const promptMarkdown = await promptEditorRef.current.blocksToMarkdownLossy(
            promptEditorRef.current.document,
          );
          if (promptMarkdown.trim()) {
            moduleParam = { generation_prompt: promptMarkdown.trim() };
          }
        } catch (err) {
          console.error('Failed to get prompt content:', err);
        }
      }

      const response = await generateModule({
        generateModuleTextRequest: {
          module_id: moduleId.trim(),
          note_id: noteId.trim(),
          thread_id: threadId || undefined,
          module: moduleParam || undefined,
        },
      }).unwrap();

      // Store thread_id for subsequent improvements
      if (response.thread_id) {
        setThreadId(response.thread_id);
        try {
          sessionStorage.setItem(STORAGE_KEYS.THREAD_ID, response.thread_id);
        } catch {
          // Silently fail
        }
      }

      if (response.stream_url) {
        await handleStream(response.stream_url);
      } else {
        setError('No stream URL in response');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start generation');
    }
  }, [moduleId, noteId, threadId, generateModule, handleStream]);

  const handleImprove = useCallback(async () => {
    if (!moduleId.trim() || !noteId.trim() || !feedback.trim()) {
      setError('Please provide module_id, note_id, and feedback');
      return;
    }

    try {
      setError(null);
      setContent('');
      setPromptContent('');
      setPromptExplanationContent('');
      setIsStreaming(false);
      setIsImproving(true);
      setLoadedVersionNumber(null); // Clear loaded version when starting new improve
      resetExplanationStream();

      // Get prompt content from editor if available
      let moduleParam: { generation_prompt: string } | undefined;
      if (promptEditorRef.current) {
        try {
          const promptMarkdown = await promptEditorRef.current.blocksToMarkdownLossy(
            promptEditorRef.current.document,
          );
          if (promptMarkdown.trim()) {
            moduleParam = { generation_prompt: promptMarkdown.trim() };
          }
        } catch (err) {
          console.error('Failed to get prompt content:', err);
        }
      }

      const response = await customizeModule({
        customizeModuleRequest: {
          module_id: moduleId.trim(),
          note_id: noteId.trim(),
          feedback: feedback.trim(),
          thread_id: customizeThreadId || undefined, // Reuse saved customize thread_id if available
          module: moduleParam || undefined,
        },
      }).unwrap();

      // Store customize thread_id for version history (in case it's a new one)
      if (response.thread_id) {
        setCustomizeThreadId(response.thread_id);
        try {
          sessionStorage.setItem(STORAGE_KEYS.CUSTOMIZE_THREAD_ID, response.thread_id);
        } catch {
          // Silently fail if sessionStorage is not available
        }
      }

      if (response.stream_url) {
        await handleStream(response.stream_url);
      } else {
        setError('No stream URL in response');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to customize module');
    }
  }, [moduleId, noteId, feedback, customizeThreadId, customizeModule, handleStream]);

  const handleStop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setIsImproving(false);
  }, []);

  // Reset customize thread_id when module_id changes
  const prevModuleIdRef = useRef<string>('');
  useEffect(() => {
    // Only reset if moduleId actually changed (not on initial mount)
    if (prevModuleIdRef.current && prevModuleIdRef.current !== moduleId) {
      setCustomizeThreadId('');
      try {
        sessionStorage.removeItem(STORAGE_KEYS.CUSTOMIZE_THREAD_ID);
      } catch {
        // Silently fail if sessionStorage is not available
      }
    }
    prevModuleIdRef.current = moduleId;
  }, [moduleId]);

  // Persist moduleId to sessionStorage (only if not from URL)
  useEffect(() => {
    if (urlModuleId) {
      // Don't persist URL-based module_id to sessionStorage
      return;
    }
    try {
      if (moduleId) {
        sessionStorage.setItem(STORAGE_KEYS.MODULE_ID, moduleId);
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.MODULE_ID);
      }
    } catch (error) {
      // Silently fail if sessionStorage is not available
    }
  }, [moduleId, urlModuleId]);

  // Persist noteId to sessionStorage
  useEffect(() => {
    try {
      if (noteId) {
        sessionStorage.setItem(STORAGE_KEYS.NOTE_ID, noteId);
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.NOTE_ID);
      }
    } catch (error) {
      // Silently fail if sessionStorage is not available
    }
  }, [noteId]);

  // Persist feedback to sessionStorage
  useEffect(() => {
    try {
      if (feedback) {
        sessionStorage.setItem(STORAGE_KEYS.FEEDBACK, feedback);
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.FEEDBACK);
      }
    } catch (error) {
      // Silently fail if sessionStorage is not available
    }
  }, [feedback]);

  // Handle drag resize for right panel
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = containerRect.right - e.clientX;
      const minWidth = 200;
      const maxWidth = containerRect.width - leftPanelWidth - 200;
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setRightPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, leftPanelWidth]);

  // Handle drag resize for left panel
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingLeft || !containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;
      const minWidth = 200;
      const maxWidth = containerRect.width - rightPanelWidth - 200;
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDraggingLeft(false);
    };

    if (isDraggingLeft) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDraggingLeft, rightPanelWidth]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (streamingUpdateTimer.current) {
        clearTimeout(streamingUpdateTimer.current);
      }
      if (promptStreamingUpdateTimer.current) {
        clearTimeout(promptStreamingUpdateTimer.current);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 2,
      }}
    >
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack spacing={2} direction="row" alignItems="center">
            <TextField
              label="Module ID"
              value={moduleId}
              onChange={e => setModuleId(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              disabled={isStreaming || isImproving || !!urlModuleId}
            />
            <TextField
              label="Note ID"
              value={noteId}
              onChange={e => setNoteId(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              disabled={isStreaming || isImproving}
            />
            {isStreaming || isImproving ? (
              <Button variant="contained" color="error" onClick={handleStop}>
                Stop
              </Button>
            ) : (
              <>
                <Button variant="contained" onClick={handleSubmit} disabled={!moduleId || !noteId}>
                  Generate
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleImprove}
                  disabled={!moduleId || !noteId || !feedback.trim()}
                >
                  Improve
                </Button>
              </>
            )}
          </Stack>
          <TextField
            label="Feedback (for improvements)"
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            size="small"
            multiline
            rows={2}
            disabled={isStreaming || isImproving}
            placeholder="Enter feedback to improve the module generation..."
          />
          {customizeThreadId && (
            <Typography variant="caption" color="text.secondary">
              Customize Thread ID: {customizeThreadId}
            </Typography>
          )}
        </Stack>
        {error && (
          <Typography color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </Paper>

      <Stack spacing={0} direction="row" sx={{ flex: 1, overflow: 'hidden' }} ref={containerRef}>
        {/* Version History Panel */}
        <Box sx={{ width: `${leftPanelWidth}px`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2">
                Version History
              </Typography>
              {loadedVersionNumber && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleSaveVersion}
                >
                  Save Version {loadedVersionNumber}
                </Button>
              )}
            </Box>
            {versionHistoryData?.version_history && versionHistoryData.version_history.length > 0 ? (
              <Stack spacing={1}>
                {versionHistoryData.version_history.map((version, index) => {
                  const versionNumber = index + 1;
                  const isCurrentVersion = versionHistoryData.current_version === versionNumber;
                  
                  return (
                    <Accordion key={index}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Typography variant="caption" sx={{ flex: 1 }}>
                            Version {versionNumber}
                            {isCurrentVersion && ' (Current)'}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={e => {
                              e.stopPropagation();
                              loadVersion(version, versionNumber);
                            }}
                            sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
                          >
                            Load
                          </Button>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          component="pre"
                          sx={{
                            fontSize: '0.75rem',
                            overflow: 'auto',
                            bgcolor: 'background.paper',
                            p: 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            maxHeight: '300px',
                          }}
                        >
                          {JSON.stringify(version, null, 2)}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No version history available
              </Typography>
            )}
          </Box>
        </Box>

        {/* Draggable resize bar for left panel */}
        <Box
          onMouseDown={() => setIsDraggingLeft(true)}
          sx={{
            width: '4px',
            cursor: 'col-resize',
            bgcolor: 'divider',
            '&:hover': {
              bgcolor: 'primary.main',
            },
            transition: 'background-color 0.2s',
          }}
        />

        {/* Main document editor */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          <BlockNoteView editor={editor} />
        </Box>

        {/* Draggable resize bar for right panel */}
        <Box
          onMouseDown={() => setIsDragging(true)}
          sx={{
            width: '4px',
            cursor: 'col-resize',
            bgcolor: 'divider',
            '&:hover': {
              bgcolor: 'primary.main',
            },
            transition: 'background-color 0.2s',
          }}
        />

        {/* Right side: Prompt explanation and Prompt editors */}
        <Box sx={{ width: `${rightPanelWidth}px`, display: 'flex', flexDirection: 'column' }}>
          {/* Prompt explanation - simple text display */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              p: 2,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.7rem' }}>
              Prompt Explanation
            </Typography>
            {isImproving && (
              <Box sx={{ mb: explanationText ? 1 : 0 }}>
                <ThinkingAnimation words={thinkingVerbs.length > 0 ? thinkingVerbs : ['Thinking...', 'Analyzing...', 'Refining...']} />
              </Box>
            )}
            {explanationText ? (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  lineHeight: 1.2,
                  color: 'text.secondary',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {explanationText}
              </Typography>
            ) : promptExplanationContent && !isImproving ? (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  lineHeight: 1.2,
                  color: 'text.secondary',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {promptExplanationContent}
              </Typography>
            ) : null}
          </Box>

          {/* Prompt editor */}
          <Box
            sx={{
              flex: 7,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              p: 2,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Prompt
            </Typography>
            <BlockNoteView editor={promptEditor} />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

